import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { HiX } from "react-icons/hi";
import { TestSettingsPanel } from "./TestSettingsPanel";
import { UseAgentTestLab } from "./useAgentTestLab";

interface TestSettingsDrawerProps {
  lab: UseAgentTestLab;
}

export const TestSettingsDrawer: React.FC<TestSettingsDrawerProps> = ({
  lab,
}) => {
  const open = lab.settingsOpen;
  const close = () => lab.setSettingsOpen(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            className="fixed inset-0 z-[70] bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={close}
          />
          <motion.aside
            key="drawer"
            role="dialog"
            aria-label="Test settings"
            className="fixed inset-y-0 right-0 z-[80] w-full sm:w-[480px] bg-[var(--card)] border-l border-[var(--border)] shadow-2xl flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.22, ease: "easeOut" }}
          >
            <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-[var(--foreground)]">
                  Test settings
                </h3>
                <p className="text-xs text-[var(--foreground-muted)] mt-0.5">
                  Provider choices, generation parameters, judge config.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close settings"
                className="text-[var(--foreground-muted)] hover:text-[var(--foreground)] p-1.5 rounded-md hover:bg-[var(--muted)]"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <TestSettingsPanel lab={lab} />
            </div>
            <div className="px-5 py-3 border-t border-[var(--border)] flex justify-end">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 text-sm bg-[var(--accent)] text-[var(--accent-foreground)] rounded-md hover:bg-[var(--accent-hover)]"
              >
                Done
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
